import clsx from 'clsx';
import capitalize from 'lodash/capitalize';
import kebabCase from 'lodash/kebabCase';
import {Subscription} from 'node_modules/react-hook-form/dist/utils/createSubject';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useFormContext} from 'react-hook-form';

import {FormFieldT, FormSelectOptionsT} from '@/lib/types/form.types';

import FormError from '@/components/form/FormError';

type FormInputPropsT<T extends object = Record<string, never>> = {
  field: FormFieldT<T>;
  fieldPrefix?: string;
  className?: string;
  hideLabel?: boolean;
  tabIndex?: number;
};

/**
 * The FormInput component is a form input component. Designed for use with `react-hook-form`. It relies on
 * the useFormContext hook and will render an input or select element based on the field type.
 * @template T
 * @param {FormFieldT<T>} field - The field object.
 * @param {string} [fieldPrefix] - Optional prefix to apply to the field name.
 * @param {string} [className] - Optional css class names to apply.
 * @param {boolean} [hideLabel] - Optional flag to hide the label.
 * @param {number} [tabIndex] - Optional tabIndex to apply to the input.
 * @returns {React.Element} - The form input element.
 */
function FormInput<T extends object = Record<string, unknown>>({
  field,
  fieldPrefix = 'form',
  className,
  hideLabel = false,
  tabIndex,
}: FormInputPropsT<T>) {
  const formContext = useFormContext();
  const {register, getFieldState, getValues, setValue, watch} = formContext;
  const {
    name,
    label,
    type = 'text',
    width = 'full',
    size = 'md',
    options,
    placeholder,
    selectOptions,
    defaultValue,
    watch: watchConfig = {},
    disabled: initialDisabled = false,
    watchConfig: fieldWatchConfig,
  } = field;

  // Get the watch config
  const {configs = [], onChange, autoCopy = !onChange} = watchConfig;

  // Calculate initial disabled state
  const initialDisabledState = (() => {
    if (fieldWatchConfig?.disableWhen) {
      const {field: watchField, is} = fieldWatchConfig.disableWhen;
      const initialValue = getValues(watchField);
      return initialValue === is;
    }
    return initialDisabled;
  })();

  // State
  const [isDisabled, setDisabled] = useState(initialDisabledState);

  // Dummy function to satisfy the type requirements of OnChangePropsT
  const setWatchEnabled = useCallback(() => null, []);

  // Get form state to check loading
  const {formState} = formContext;
  const isLoading = formState.isSubmitting;

  // Computed
  const fieldName = String(name);
  const fieldNameWithPrefix = kebabCase(`${fieldPrefix}-${fieldName}`);
  const fieldState = getFieldState(fieldName);
  const fieldValue = getValues(fieldName);

  // Refs
  const fieldSubscriptionRef = useRef<Subscription | null>(null);

  // --------------------------------------------------------------------------------------------
  // WATCH CONFIG
  // --------------------------------------------------------------------------------------------
  // Effect for field's own onChange
  useEffect(() => {
    if (onChange) {
      fieldSubscriptionRef.current = watch((values, {name: changedField}) => {
        if (changedField === fieldName) {
          onChange({value: values[fieldName], formContext, field, setWatchEnabled});
        }
      });
    }

    return () => {
      if (fieldSubscriptionRef.current) {
        fieldSubscriptionRef.current.unsubscribe();
      }
    };
  }, [watch, fieldName, onChange, field, formContext, setWatchEnabled]);

  // Effect for handling disabled state from watchConfig
  useEffect(() => {
    if (fieldWatchConfig?.disableWhen) {
      const {field: watchField, is} = fieldWatchConfig.disableWhen;

      // Subscribe to changes
      const subscription = watch(values => {
        const watchValue = values[watchField];
        setDisabled(watchValue === is);
      });

      return () => subscription.unsubscribe();
    }
  }, [watch, fieldWatchConfig]);

  // Effect for handling autoCopy from watchConfig
  useEffect(() => {
    if (autoCopy) {
      setValue(fieldName, fieldValue);
    }
  }, [autoCopy, fieldValue, setValue, fieldName]);

  // Effect for handling watch configs
  useEffect(() => {
    if (configs.length) {
      configs.forEach(config => {
        const {source, target, enabled, autoCopy} = config;
        const sourceValue = getValues(source);
        if (enabled && autoCopy && target) {
          setValue(target, sourceValue);
        }
      });
    }
  }, [configs, getValues, setValue]);

  // Register the field - type:static is not registered
  const registerProps =
    type === 'static'
      ? {}
      : type === 'checkbox'
        ? {
            ...register(fieldName, {
              ...options,
              required: options?.required
                ? typeof options?.required === 'string'
                  ? options?.required
                  : 'Field is required'
                : false,
              disabled: isDisabled,
            }),
            defaultChecked: fieldValue ?? defaultValue,
          }
        : register(fieldName, {
            ...options,
            required: options?.required
              ? typeof options?.required === 'string'
                ? options?.required
                : 'Field is required'
              : false,
            disabled: isDisabled,
          });

  // Modify the disabled state check to include the disabledFields map and loading state
  const isFieldDisabled = isDisabled || (fieldWatchConfig?.disabledWhenLoading && isLoading) || false;

  return (
    <div
      className={clsx(
        {
          'form-check': type === 'checkbox',
          'form-check-lg': type === 'checkbox' && size === 'lg',
          'form-check-sm': type === 'checkbox' && size === 'sm',
        },
        className
      )}
    >
      {!hideLabel && label && type !== 'checkbox' && (
        <label
          htmlFor={fieldNameWithPrefix}
          className={clsx({
            'ti-form-label': type !== 'select',
            'ti-form-select-label': type === 'select',
          })}
        >
          {label}
        </label>
      )}
      {(() => {
        switch (type) {
          // Select element ---------------------------------------------------------------------
          case 'select':
            return (
              <select
                className={clsx('ti-form-select', {
                  '!border-red': !!fieldState.error,
                  'cursor-not-allowed bg-gray-100 opacity-60': isFieldDisabled,
                })}
                id={fieldNameWithPrefix}
                defaultValue={fieldValue ?? placeholder ?? ''}
                disabled={isFieldDisabled}
                tabIndex={tabIndex}
                {...registerProps}
              >
                {placeholder && <option value="">{capitalize(placeholder ?? label)}</option>}
                {selectOptions?.map((option: FormSelectOptionsT, index: number) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            );
          case 'textarea':
            return (
              <textarea
                className={clsx('ti-form-control', {
                  'w-full': width === 'full',
                  '!border-red': !!fieldState.error,
                  'cursor-not-allowed bg-gray-100 opacity-60': isFieldDisabled,
                })}
                id={fieldNameWithPrefix}
                placeholder={capitalize(placeholder ?? label)}
                defaultValue={fieldValue}
                disabled={isFieldDisabled}
                tabIndex={tabIndex}
                {...registerProps}
              />
            );
          case 'date': {
            return (
              <input
                type="date"
                className={clsx('ti-form-control', {
                  'w-full': width === 'full',
                  '!border-red': !!fieldState.error,
                  'cursor-not-allowed bg-gray-100 opacity-60': isFieldDisabled,
                })}
                id={fieldNameWithPrefix}
                {...registerProps}
                defaultValue={
                  !fieldValue
                    ? undefined
                    : typeof fieldValue === 'string'
                      ? fieldValue.split('T')[0] // Handle ISO date strings
                      : new Date(fieldValue).toISOString().split('T')[0] // Handle Date objects
                }
                disabled={isFieldDisabled}
                tabIndex={tabIndex}
              />
            );
          }
          case 'checkbox': {
            return (
              <input
                type="checkbox"
                className={clsx('form-check-input', {
                  'mt-0.5': size === 'md' || size === 'sm',
                  '!border-red': !!fieldState.error,
                  'cursor-not-allowed opacity-60': isFieldDisabled,
                })}
                id={fieldNameWithPrefix}
                {...register(fieldName)}
                disabled={isFieldDisabled}
                tabIndex={tabIndex}
              />
            );
          }

          case 'password':
            return (
              <input
                type="password"
                className={clsx('ti-form-control', {
                  'w-full': width === 'full',
                  '!border-red': !!fieldState.error,
                  'cursor-not-allowed bg-gray-100 opacity-60': isFieldDisabled,
                })}
                id={fieldNameWithPrefix}
                placeholder={placeholder}
                {...registerProps}
                disabled={isFieldDisabled}
                tabIndex={tabIndex}
                autoComplete="off"
              />
            );
          case 'static':
            return isFieldDisabled ? null : field.content || String(fieldValue);

          default:
            // Default to text input -------------------------------------------------------------
            return (
              <input
                type={type}
                className={clsx({
                  'ti-form-control': size === 'md',
                  'ti-form-control-sm': size === 'sm',
                  'ti-form-control-lg': size === 'lg',
                  'w-full': width === 'full',
                  '!border-red': !!fieldState.error,
                  'cursor-not-allowed bg-gray-100 opacity-60': isFieldDisabled,
                })}
                id={fieldNameWithPrefix}
                placeholder={capitalize(placeholder ?? label)}
                defaultValue={fieldValue}
                disabled={isFieldDisabled}
                tabIndex={tabIndex}
                {...registerProps}
              />
            );
        }
      })()}
      {type === 'checkbox' && (
        <label htmlFor={fieldNameWithPrefix} className="form-check-label cursor-pointer">
          {label}
        </label>
      )}
      {fieldState.error && <FormError error={fieldState.error} />}
    </div>
  );
}

export default FormInput;
